import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { AuthInteractionStatus } from "./cognito-browser";

const CognitoContext = createContext({
  instance: null,
  accounts: [],
  inProgress: AuthInteractionStatus.None,
  isAuthenticated: false,
});

export const CognitoProvider = ({ instance, children }) => {
  const [accounts, setAccounts] = useState(() =>
    instance?.getAllAccounts ? instance.getAllAccounts() : [],
  );
  const [inProgress, setInProgress] = useState(() =>
    instance?.getInProgress ? instance.getInProgress() : AuthInteractionStatus.None,
  );

  useEffect(() => {
    let isMounted = true;
    if (!instance) {
      return undefined;
    }

    const syncFromInstance = () => {
      if (!isMounted) {
        return;
      }
      setAccounts(instance.getAllAccounts());
      setInProgress(instance.getInProgress());
    };

    // Sync immediately to avoid a one-render "None" race during redirect callback.
    syncFromInstance();
    instance.initialize().then(syncFromInstance).catch(console.error);
    const removeListener = instance.addStateListener(syncFromInstance);
    return () => {
      isMounted = false;
      removeListener();
    };
  }, [instance]);

  const value = useMemo(
    () => ({
      instance,
      accounts,
      inProgress,
      isAuthenticated: accounts.length > 0,
    }),
    [accounts, inProgress, instance],
  );

  return <CognitoContext.Provider value={value}>{children}</CognitoContext.Provider>;
};

export const useCognito = () => useContext(CognitoContext);

export const useIsAuthenticated = () => {
  const { isAuthenticated } = useContext(CognitoContext);
  return isAuthenticated;
};
